import React, { useState } from 'react';

/**
 * 1-Click Copy Micro-Interaction Component
 * Copies text to clipboard, morphs icon into a green checkmark,
 * and displays a floating "Copied!" micro-tooltip.
 */
export default function CopyButton({
  text = '',
  label,
  copiedLabel = 'Copied!',
  variant = 'pill', // 'pill', 'icon', 'button'
  iconSize = 14,
  title = 'Copy to clipboard',
  onCopied,
  style = {}
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!text) return;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }

      setCopied(true);
      if (onCopied) onCopied(text);

      setTimeout(() => {
        setCopied(false);
      }, 1800);
    } catch (err) {
      console.warn('⚠️ Clipboard copy error:', err);
    }
  };

  const getVariantStyle = () => {
    if (variant === 'icon') {
      return {
        ...styles.baseIconBtn,
        ...(copied ? styles.copiedIconBtn : {})
      };
    }
    if (variant === 'button') {
      return {
        ...styles.baseButton,
        ...(copied ? styles.copiedButton : {})
      };
    }
    // Default: 'pill'
    return {
      ...styles.basePill,
      ...(copied ? styles.copiedPill : {})
    };
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      style={{ ...getVariantStyle(), ...style }}
      title={title}
    >
      {/* Icon with morph transition */}
      {copied ? (
        <svg
          width={iconSize}
          height={iconSize}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#10b981"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={styles.popIcon}
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg
          width={iconSize}
          height={iconSize}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
        </svg>
      )}

      {/* Label Text */}
      {(label || copied) && (
        <span style={{ ...styles.label, ...(copied ? styles.copiedLabel : {}) }}>
          {copied ? copiedLabel : label}
        </span>
      )}

      {/* Floating Micro-Tooltip */}
      {copied && (
        <div style={styles.floatingTooltip}>
          <span>✓ Copied!</span>
          <div style={styles.tooltipArrow} />
        </div>
      )}
    </button>
  );
}

const styles = {
  basePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px 12px',
    borderRadius: '8px',
    border: '1px solid var(--border-color, #e2e8f0)',
    background: 'var(--bg-card, #ffffff)',
    color: 'var(--text-secondary, #475569)',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
    userSelect: 'none'
  },
  copiedPill: {
    borderColor: '#10b981',
    background: 'rgba(16, 185, 129, 0.08)',
    color: '#059669',
    transform: 'scale(1.02)'
  },
  baseIconBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: '1px solid var(--border-color, #e2e8f0)',
    background: 'var(--bg-card, #ffffff)',
    color: 'var(--text-muted, #64748b)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
    userSelect: 'none'
  },
  copiedIconBtn: {
    borderColor: '#10b981',
    background: 'rgba(16, 185, 129, 0.1)',
    color: '#059669',
    transform: 'scale(1.05)'
  },
  baseButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '10px',
    border: '1px solid var(--border-color, #e2e8f0)',
    background: 'var(--bg-card, #ffffff)',
    color: 'var(--text-secondary, #334155)',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
    userSelect: 'none'
  },
  copiedButton: {
    borderColor: '#10b981',
    background: 'rgba(16, 185, 129, 0.08)',
    color: '#059669',
    transform: 'scale(1.02)'
  },
  popIcon: {
    animation: 'popIn 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
  },
  label: {
    lineHeight: 1
  },
  copiedLabel: {
    color: '#059669',
    fontWeight: '700'
  },
  floatingTooltip: {
    position: 'absolute',
    bottom: 'calc(100% + 8px)',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#0f172a',
    color: '#ffffff',
    fontSize: '11px',
    fontWeight: '700',
    padding: '4px 8px',
    borderRadius: '6px',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    zIndex: 999
  },
  tooltipArrow: {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    borderWidth: '4px',
    borderStyle: 'solid',
    borderColor: '#0f172a transparent transparent transparent'
  }
};
